<template>
  <!-- Account button and menu in the top bar (App.vue). Opens on click, Enter or Space, never on hover -->
  <div ref="rootEl" class="relative">
    <button ref="triggerEl" type="button" class="topbar-btn topbar-avatar-btn"
      :aria-label="`Account: ${name}`" aria-haspopup="menu" :aria-expanded="open" aria-controls="account-menu"
      @click="onTriggerClick" @keydown.down.prevent="openMenu(true)">
      <span class="topbar-avatar">{{ initials || '?' }}</span>
    </button>
  <!-- Menu -->
    <div v-if="open" id="account-menu" role="menu" aria-label="Account" class="account-menu" @keydown="onMenuKeydown">
    <!-- Header -->
      <div class="account-menu-head">
        <span class="topbar-avatar account-menu-avatar">{{ initials || '?' }}</span>
        <div class="min-w-0 flex flex-col">
          <div class="account-menu-name">{{ name }}</div>
          <div v-if="orgName" class="account-menu-org">{{ orgName }}</div>
        </div>
      </div>
    <!-- Items -->
      <button v-for="item in items" :key="item.link" ref="itemEls" type="button" role="menuitem" tabindex="-1"
        class="account-menu-item" @click="go(item.link, $event)">
        <component :is="item.icon" /><span>{{ item.title }}</span>
      </button>
      <div class="account-menu-sep" role="separator"></div>
    <!-- Log Out -->
      <button ref="logoutEl" type="button" role="menuitem" tabindex="-1" class="account-menu-item text-red" @click="signOut">
        <LogoutIcon /><span>Log Out</span>
      </button>
    </div>
  </div>
</template>

<script>
import { computed, inject, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { UserCircleIcon, CalendarIcon, ClockIcon, CheckCircleIcon, InformationCircleIcon } from '@heroicons/vue/solid'
import { LogoutIcon } from '@heroicons/vue/outline'

export default {
  name: 'AccountMenu',
  components: {
    LogoutIcon
  },
  setup() {
    const global = inject('global')
    const { authenticated, initials, logout } = global
    const route = useRoute()
    const router = useRouter()

  // Refs
    const open = ref(false)
    const rootEl = ref(null)
    const triggerEl = ref(null)
    const itemEls = ref([])
    const logoutEl = ref(null)

  // Computed
    const name = computed(() => authenticated.value?.name ?? '')
    const orgName = computed(() => authenticated.value?.organization?.name ?? '')
    const items = computed(() => {
      return [
        { title: 'My Account', link: '/myAccount', icon: UserCircleIcon, condition: true },
        { title: 'Time Off', link: '/pto', icon: CalendarIcon, condition: authenticated.value?.role?.accessModel?.pto > 0 },
        { title: 'Time Clock', link: '/timeclock', icon: ClockIcon, condition: authenticated.value?.timesheet },
        { title: 'Timesheets', link: '/timecards', icon: CheckCircleIcon, condition: authenticated.value?.timesheet },
        { title: 'About', link: '/about', icon: InformationCircleIcon, condition: true }
      ].filter(item => item.condition)
    })

  // Helpers
    const menuItems = () => [...(itemEls.value ?? []), logoutEl.value].filter(Boolean)

    const onDocumentPointerdown = (e) => {
      if (rootEl.value && !rootEl.value.contains(e.target)) { closeMenu(false) }
    }

    // Esc closes the menu wherever focus is: a mouse click leaves it on the trigger, outside the menu. Capture phase, and
    // marked, so it runs before the phone side-menu Escape handler in App.vue, which then leaves it alone.
    const onDocumentKeydown = (e) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return
      e.preventDefault()
      closeMenu(true)
    }

    const openMenu = (focusFirst) => {
      open.value = true
      document.addEventListener('pointerdown', onDocumentPointerdown)
      document.addEventListener('keydown', onDocumentKeydown, true)
      nextTick(() => {
        if (focusFirst) { menuItems()[0]?.focus() }
      })
    }

    const closeMenu = (returnFocus) => {
      open.value = false
      document.removeEventListener('pointerdown', onDocumentPointerdown)
      document.removeEventListener('keydown', onDocumentKeydown, true)
      if (returnFocus) { triggerEl.value?.focus() }
    }

  // Watchers
    watch(() => route.fullPath, () => {
      if (open.value) { closeMenu(false) }
    })

  // Lifecycle
    onBeforeUnmount(() => {
      document.removeEventListener('pointerdown', onDocumentPointerdown)
      document.removeEventListener('keydown', onDocumentKeydown, true)
    })

  // Handlers
    // e.detail is 0 when Enter or Space activated the button, so only the keyboard moves focus into the menu.
    const onTriggerClick = (e) => {
      if (open.value) { closeMenu(false) }
      else { openMenu(e?.detail === 0) }
    }

    const onMenuKeydown = (e) => {
      const list = menuItems()
      if (!list.length) return
      const index = list.indexOf(document.activeElement)
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        list[(index + 1) % list.length].focus()
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        list[(index - 1 + list.length) % list.length].focus()
      } else if (e.key === 'Home') {
        e.preventDefault()
        list[0].focus()
      } else if (e.key === 'End') {
        e.preventDefault()
        list[list.length - 1].focus()
      } else if (e.key === 'Tab') {
        closeMenu(false)
      }
    }

    const go = (link, e) => {
      closeMenu(e?.detail === 0)
      router.push(link)
    }

    const signOut = () => {
      closeMenu(false)
      logout()
    }

    return {
      open,
      rootEl,
      triggerEl,
      itemEls,
      logoutEl,
      name,
      orgName,
      initials,
      items,
      openMenu,
      onTriggerClick,
      onMenuKeydown,
      go,
      signOut
    }
  }
}
</script>
